<script setup lang="ts">
import { StoryblokComponent } from '@storyblok/vue';
import { computed, ref } from 'vue';

interface StoryblokChild {
    _uid: string;
    component?: string;
}

interface TestimonialSectionBlok {
    _uid: string;
    headline: string;
    headline_emphasis: string;
    testimonials: StoryblokChild[];
}

const props = defineProps<{ blok: TestimonialSectionBlok }>();

const items = computed(() => props.blok.testimonials ?? []);

const currentIndex = ref(0);

const slideStyle = computed(() => ({
    transform: `translateX(-${currentIndex.value * 100}%)`,
    transition: 'transform 0.4s ease',
}));

function next() {
    if (currentIndex.value < items.value.length - 1) currentIndex.value++;
}
function prev() {
    if (currentIndex.value > 0) currentIndex.value--;
}

let touchStartX = 0;
let touchEndX = 0;

function onTouchStart(e: TouchEvent) {
    touchStartX = e.changedTouches[0].screenX;
}
function onTouchMove(e: TouchEvent) {
    touchEndX = e.changedTouches[0].screenX;
}
function onTouchEnd() {
    const diff = touchStartX - touchEndX;
    const threshold = 50;
    if (diff > threshold) next();
    else if (diff < -threshold) prev();
}
</script>

<template>
    <section v-editable="blok" class="relative z-10 font-quicksand text-[#335454]">
        <div class="relative mx-auto flex max-w-[1920px] flex-col px-3 pb-8 pt-7 xl:px-10 xl:pb-[75px] xl:pt-[75px]">
            <div class="flex flex-col justify-center">
                <h2 class="mb-6 text-center text-[28px] font-semibold leading-tight text-[#004646] xl:mb-8 xl:text-4xl">
                    <span v-if="blok.headline">{{ blok.headline }}</span>
                    <template v-if="blok.headline && blok.headline_emphasis">&nbsp;</template>
                    <b v-if="blok.headline_emphasis" class="font-bold">{{ blok.headline_emphasis }}</b>
                </h2>
                <div v-if="items.length" class="relative xl:px-[100px]">
                    <div class="carousel" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd">
                        <div class="slides" :style="slideStyle">
                            <div v-for="child in items" :key="child._uid" class="testimonial flex flex-col gap-4 xl:flex-row">
                                <StoryblokComponent :blok="child" />
                            </div>
                        </div>
                    </div>

                    <div>
                        <button
                            type="button"
                            class="btn btn-prev hidden h-[52px] w-[52px] items-center justify-center rounded-full bg-white xl:flex"
                            :class="{ '!hidden': currentIndex === 0 }"
                            aria-label="Vorheriges Testimonial"
                            @click="prev"
                        >
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M15 18.6958L9 12.6958L15 6.6958"
                                    stroke="#335454"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                        <button
                            type="button"
                            class="btn btn-next hidden h-[52px] w-[52px] items-center justify-center rounded-full bg-white xl:flex"
                            :class="{ '!hidden': currentIndex === items.length - 1 }"
                            aria-label="Nächstes Testimonial"
                            @click="next"
                        >
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M9 18.6958L15 12.6958L9 6.6958"
                                    stroke="#335454"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                    <div class="mt-6 flex items-center justify-center gap-3">
                        <span
                            v-for="(_, index) in items.length"
                            :key="index"
                            class="h-4 w-[62px] rounded-[36px] xl:h-[6px]"
                            :class="index === currentIndex ? 'bg-[#F5A77D]' : 'bg-[#FDEDE5]'"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.carousel {
    overflow: hidden;
    margin: auto;
    position: relative;
}

.slides {
    display: flex;
    width: 100%;
}

.testimonial {
    min-width: 100%;
    box-sizing: border-box;
    padding-left: 15px;
    padding-right: 15px;
}

.btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
}

.btn-prev {
    left: 0;
}

.btn-next {
    right: 0;
}
</style>
